import { useEffect, useMemo, useRef, useState } from 'react';

import { fetchCardsWithCache } from '../../combat/api/cards';
import { normalizeCard } from '../../combat/game/types';
import { COMBAT_VFX_PRELOAD_SOURCES } from '../../combat/config/vfx';
import { tarotCards } from '../../tarot/cards';
import {
  loadImage,
  uniq,
  warmImagesInIdleBatches,
} from '../../utils/preloadAssets';
import { relicsPool } from '../data/relics';

export function useBootLoader({
  localAssets,
  combatLibrary,
  combatLoadCards,
  combatDispatch,
}) {
  const [bootReady, setBootReady] = useState(false);
  const [bootProgress, setBootProgress] = useState({
    loaded: 0,
    total: 0,
    phase: 'boot',
  });

  const combatPreloadPromiseRef = useRef(null);
  const tilesLoadedRef = useRef(false);

  const stableLocalAssets = useMemo(
    () => uniq((Array.isArray(localAssets) ? localAssets : []).filter(Boolean)),
    [localAssets]
  );

  // Extract lists of remote image assets. Combat card images are critical
  // for the first combat experience, while relic and tarot images can be
  // warmed asynchronously after boot.
  const combatCardAssets = useMemo(
    () =>
      uniq(
        (Array.isArray(combatLibrary) ? combatLibrary : []).map((c) => c?.img)
      ),
    [combatLibrary]
  );

  const deferredGalleryAssets = useMemo(
    () => uniq([
        ...relicsPool.map((r) => r?.image),
        ...tarotCards.map((t) => t?.image),
      ]),
    []
  );

  const combatVfxAssets = useMemo(
    () => uniq(COMBAT_VFX_PRELOAD_SOURCES),
    []
  );

  const bootPct = useMemo(
    () =>
      bootProgress.total > 0
        ? Math.round((bootProgress.loaded / bootProgress.total) * 100)
        : 0,
    [bootProgress]
  );

  // Preload combat card library once when the app boots so combat opens instantly.
  // Keep the in-flight promise in a ref so React Strict Mode can remount this
  // effect without losing the preload result. The previous boolean guard caused
  // the first mount to start the request, the cleanup to mark it as unmounted,
  // and the second mount to skip the request entirely.
  useEffect(() => {
    let cancelled = false;

    if (Array.isArray(combatLibrary) && combatLibrary.length > 0) return undefined;

    if (!combatPreloadPromiseRef.current) {
      combatPreloadPromiseRef.current = fetchCardsWithCache(30 * 60 * 1000).then(
        (raw) =>
          raw.map((r) => normalizeCard({ ...r, id: r.id ?? r._id }))
      );
    }

    combatPreloadPromiseRef.current
      .then((cards) => {
        if (cancelled) return;
        combatLoadCards(cards);
      })
      .catch((e) => {
        if (cancelled) return;
        combatDispatch({
          type: 'LOAD_ERROR',
          error: e?.message ?? 'Failed to load cards',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [combatDispatch, combatLoadCards, combatLibrary]);

  useEffect(() => {
    let cancelled = false;

    const makeProgressUpdater = (phase, total) => {
      let loaded = 0;
      let lastEmit = 0;
      const emitEveryN = 10; // update UI every N assets
      const emitEveryMs = 80; // and at most every ~N ms

      return () => {
        loaded += 1;
        const now = Date.now();
        const shouldEmit =
          loaded === 1 ||
          loaded === total ||
          (loaded % emitEveryN === 0) ||
          (now - lastEmit > emitEveryMs);

        if (shouldEmit && !cancelled) {
          lastEmit = now;
          setBootProgress({ loaded, total, phase });
        }
      };
    };

    const waitForLibrary = () =>
      new Promise((resolve) => {
        if (Array.isArray(combatLibrary) && combatLibrary.length > 0) {
          resolve();
          return;
        }

        const startedAt = Date.now();
        const maxWaitMs = 15000;

        const t = setInterval(() => {
          if (cancelled) {
            clearInterval(t);
            return;
          }

          if (Array.isArray(combatLibrary) && combatLibrary.length > 0) {
            clearInterval(t);
            resolve();
            return;
          }

          if (Date.now() - startedAt > maxWaitMs) {
            clearInterval(t);
            resolve();
          }
        }, 50);
      });

    (async () => {
      // Phase 1: preload local tiles/UI once
      if (!tilesLoadedRef.current) {
        const tickTiles = makeProgressUpdater('tiles', stableLocalAssets.length);

        setBootProgress({
          loaded: 0,
          total: stableLocalAssets.length,
          phase: 'tiles',
        });

        await Promise.all(
          stableLocalAssets.map((src) =>
            loadImage(src).finally(() => {
              tickTiles();
            })
          )
        );

        tilesLoadedRef.current = true;
      }

      // Phase 2: wait for combat library
      await waitForLibrary();

      // If still not loaded (timeout), let the game start anyway
      if (!Array.isArray(combatLibrary) || combatLibrary.length === 0) {
        if (!cancelled) setBootReady(true);
        return;
      }

      // Phase 3: preload only combat card art. Gallery images (relics + tarot)
      // are warmed later in idle time. This reduces boot pressure without
      // impacting first-combat readiness.
      const bootCriticalRemoteAssets = combatCardAssets.filter(Boolean);

      if (bootCriticalRemoteAssets.length > 0) {
        const tickCards = makeProgressUpdater(
          'cards',
          bootCriticalRemoteAssets.length
        );

        setBootProgress({
          loaded: 0,
          total: bootCriticalRemoteAssets.length,
          phase: 'cards',
        });

        await Promise.all(
          bootCriticalRemoteAssets.map((src) =>
            loadImage(src).finally(() => {
              tickCards();
            })
          )
        );
      }

      if (combatVfxAssets.length > 0) {
        const tickCombatVfx = makeProgressUpdater(
          'combat-vfx',
          combatVfxAssets.length
        );

        setBootProgress({
          loaded: 0,
          total: combatVfxAssets.length,
          phase: 'combat-vfx',
        });

        await Promise.all(
          combatVfxAssets.map((src) =>
            loadImage(src).finally(() => {
              tickCombatVfx();
            })
          )
        );
      }

      if (!cancelled) setBootReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [combatLibrary, combatCardAssets, combatVfxAssets, stableLocalAssets]);

  // Stop main menu music once the game finishes boot loading
  useEffect(() => {
    if (!bootReady) return;
    if (typeof window === 'undefined') return;

    const a = window.__dd_menuMusic;
    if (!a) return;

    const durationMs = 1900;
    const startVol = typeof a.volume === 'number' ? a.volume : 0.55;
    const start =
      typeof performance !== 'undefined' ? performance.now() : Date.now();

    let raf = 0;

    const tick = (now) => {
      const nowMs =
        typeof now === 'number'
          ? now
          : typeof performance !== 'undefined'
          ? performance.now()
          : Date.now();

      const t = Math.min(1, (nowMs - start) / durationMs);

      try {
        a.volume = startVol * (1 - t);
      } catch {}

      if (t < 1) {
        raf = requestAnimationFrame(tick);
        return;
      }

      try {
        a.pause();
        a.currentTime = 0;
        a.volume = 0;
        a.src = '';
      } catch {}

      try {
        window.__dd_menuMusic = null;
      } catch {}
    };

    raf = requestAnimationFrame(tick);

    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [bootReady]);

  // After boot is ready, warm relic/tarot gallery images during idle time.
  useEffect(() => {
    if (!bootReady) return undefined;

    const deferredSources = deferredGalleryAssets.filter(Boolean);
    if (!deferredSources.length) return undefined;

    return warmImagesInIdleBatches(deferredSources, {
      batchSize: 6,
      timeoutMs: 150,
      fallbackDelayMs: 32,
    });
  }, [bootReady, deferredGalleryAssets]);

  return {
    bootReady,
    bootProgress,
    bootPct,
  };
}